import { GetSelectedApplicants } from '@type/bulk-operations';
import { PostBulkBaseObject } from '@type/v1-api-types';
import { useState } from 'react';
import { useBjModal } from '@hooks/common/useBjModal';
import { bjToast } from '@components/common';
import { useTranslation } from 'next-i18next';
import { useSearchParams } from 'next/navigation';


/**
 * @description Handle the bulk applicants modal toggle and base post object build. If no applicants are selected, a toast message is shown.
 * - **getSelectedApplicants**: Get the selected applicants. Function that returns an array of selected applicants.
 * - **selectAll**: Boolean value that indicates if all applicants are selected.
 * - **status**: Status of the applicants.
 * - **queryParams**: The filters query params object.
 *
 * @returns Object with the following properties:
 * - **isOpen**: Boolean value that indicates if the modal is open.
 * - **toggleBulkModal**: Function that toggles the modal.
 * - **getPostObject**: Function that returns the selected applicants. If no applicants are selected, a toast message is shown.
 * - **closeOnSuccess**: Function that closes the modal. Pass it to SWR's onSuccess callback.
 * - **basePostObject**: Base object for POST operations. Add it as 'wizardData' in useBjForm, so it will be merged with the form data.
 *
 * @example
 * const { isOpen, toggleBulkModal, closeOnSuccess, basePostObject } = useOpenBulkModal({ getSelectedApplicants, selectAll, status });
 */
export const useOpenBulkModal = (props: GetSelectedApplicants) => {
  const {
    getSelectedApplicants,
    selectAll,
    status = 0,
    selectByColumn,
    queryParams,
    normalizeSelectedApplicantsForBulkAction,
  } = props;

  // Helpers
  const { t } = useTranslation('common');
  const { isOpen, toggleModal, closeModal } = useBjModal();

  // Check if filters are applied; If so, we need to build the list by slugs
  const paramIsStatus = queryParams && Object.keys(queryParams).length === 1 && queryParams?.['status' as keyof typeof queryParams];
  const hasFiltersApplied = queryParams ? Object.keys(queryParams).length > 0 && !paramIsStatus : false;


  // Base object for POST operations
  const [basePostObject, setBasePostObject] = useState<PostBulkBaseObject | undefined>(undefined);

  // Build the post object
  const searchParams = useSearchParams(); // top-level of your hook
  const queryParamsObject = searchParams ? Object.fromEntries(searchParams.entries()) : {};

  const getNormalizedSelectedApplicants = () => {
    const selectedApplicants = getSelectedApplicants();
    return normalizeSelectedApplicantsForBulkAction
      ? normalizeSelectedApplicantsForBulkAction(selectedApplicants)
      : selectedApplicants;
  };

  const buildPostObject = () => {
    const normalizedSelectedApplicants = getNormalizedSelectedApplicants();
    const filtersArray = Array.isArray(selectByColumn) && selectByColumn.length > 0
      ? selectByColumn.map(({ status: colStatus, statusFilter }) => {
        const combinedFilters = {
          ...queryParamsObject,
          ...(statusFilter.length ? { statusFilter } : {}),
        };

        return {
          boardStatus: colStatus === 'bsf_unselected' ? 'rejected' : colStatus,
          filters: combinedFilters,
        };
      })
      : undefined;

    return {
      ...((!selectAll || (selectAll && hasFiltersApplied)) && {
        slugs: normalizedSelectedApplicants,
      }),
      ...(selectAll && !hasFiltersApplied && { buildListByStatus: Number(status) }),
      ...(filtersArray && filtersArray.length > 0 ? { filters: filtersArray } : {}),
    };
  };


  // Toggle bulk modal
  // If no applicants are selected, show a toast message
  const toggleBulkModal = () => {
    const selectedApplicants = getNormalizedSelectedApplicants();
    if (selectedApplicants.length > 0 || (selectAll && !hasFiltersApplied)) {
      toggleModal();
      setBasePostObject(buildPostObject());
    } else {
      bjToast.error(t('cv.action.bulk.select_cv'));
    }
  };

  // Get the post object if not running the operation in a modal
  const getPostObject = () => {
    const selectedApplicants = getNormalizedSelectedApplicants();
    if (selectedApplicants.length > 0 || (selectAll && !hasFiltersApplied)) {
      const postObject = buildPostObject();
      const hasSelectedSlugs = Array.isArray(postObject.slugs) && postObject.slugs.length > 0;

      if (hasSelectedSlugs || (selectAll && !hasFiltersApplied)) {
        setBasePostObject(postObject);
        return postObject;
      }
    }
    // If no applicants are selected, show a toast message
    bjToast.error(t('cv.action.bulk.select_cv'));
    return undefined;
  };

  // Delayed close modal to be used after a successful operation
  const closeOnSuccess = () => {
    setTimeout(() => {
      closeModal();
    }, 200);
  };

  return {
    isOpen,
    toggleBulkModal,
    getPostObject,
    closeOnSuccess,
    basePostObject,
  };
};
